/** Commerce operations (spec §11). */

import { z } from "zod";
import { internalOp, publicOp, readOp, writeOp, zEpochMs, zId, zMoney, zNonNegMoney, zRecipient } from "./common.js";
import { GTM_CONTAINER_ID } from "./gtm.js";
import { zFunnelSurface } from "./funnel.js";

/** Totals are server-derived — a client-supplied total is not in the vocabulary. */

/**
 * B170 amendment (§10.2): a promotion that changes what the buyer pays is
 * an OUTCOME, and outcomes are stated — every fired promotion crosses the
 * wire as a named row. `label` is the promotion's display name (buyer
 * words, never an id); `amount` is signed and ≤ 0 (money off — a positive
 * "adjustment" would be a surcharge wearing a discount's face; zero is
 * legal, a waiver can net zero). A total that moved with no row to explain
 * it is wrong data on a buyer money face.
 */
const zOffMoney = z
  .object({ amount: z.number().int().safe().nonpositive(), currency: z.string().regex(/^[A-Z]{3}$/) })
  .strict();
const zAdjustment = z.object({ promotionId: zId, label: z.string().min(1), amount: zOffMoney }).strict();

/**
 * B170 amendment (§10.1 display): totals explain themselves. `subtotal` is
 * the sum of line amounts BEFORE promotions (post-policy); the arithmetic
 * closes on screen: total = subtotal + Σ adjustments + (feeTotal ?? 0)
 * + (taxTotal ?? 0). `feeTotal` (§10.1, customer-borne) and `taxTotal`
 * (§10.3, exclusive taxes charged on top — inclusive taxes live inside
 * line amounts) appear only when they charge anything.
 */
const zTotals = z
  .object({
    total: zNonNegMoney,
    subtotal: zNonNegMoney,
    adjustments: z.array(zAdjustment),
    feeTotal: zNonNegMoney.optional(),
    taxTotal: zNonNegMoney.optional(),
  })
  .strict();
const exampleTotals = {
  total: { amount: 5670, currency: "USD" },
  subtotal: { amount: 6300, currency: "USD" },
  adjustments: [{ promotionId: "promo-1", label: "Summer 10% off", amount: { amount: -630, currency: "USD" } }],
};

export const cartCreate = writeOp({
  name: "cart.create",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { cartId: zId, channelId: zId, currency: z.string().regex(/^[A-Z]{3}$/) },
  response: z.object({ cartId: zId }).strict(),
  exampleRequest: { cartId: "cart-1", channelId: "web", currency: "USD" },
  exampleResponse: { cartId: "cart-1" },
});

/**
 * B20b(c) ruling 2026-07-14: cart responses name their lines. cart.add
 * returns the lineId it minted, cart.remove echoes the one it removed —
 * without these, cart.remove is undriveable from the §11 surface.
 *
 * B391 ruling (Pathik, 2026-08-19), stated here because a caller cannot
 * infer it: **cart.add does NOT merge.** A repeat add of the same product,
 * variant and slot mints a SECOND line with its own id; it never raises the
 * count on an existing one. So a quantity stepper is `cart.set_quantity`,
 * never a second add — pressing `+` on `cart.add` would grow the cart a row
 * at a time. Both the till's sale lines and the storefront cart wear this
 * rule.
 */
export const cartAdd = writeOp({
  name: "cart.add",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { cartId: zId, productId: zId, variantId: zId.optional(), slotId: zId.optional(), quantity: z.number().int().positive() },
  response: z.object({ cartId: zId, lineId: zId, totals: zTotals }).strict(),
  exampleRequest: { cartId: "cart-1", productId: "p-1", quantity: 2 },
  exampleResponse: { cartId: "cart-1", lineId: "line-1", totals: exampleTotals },
});

export const cartRemove = writeOp({
  name: "cart.remove",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { cartId: zId, lineId: zId },
  response: z.object({ cartId: zId, lineId: zId, totals: zTotals }).strict(),
  exampleRequest: { cartId: "cart-1", lineId: "line-1" },
  exampleResponse: { cartId: "cart-1", lineId: "line-1", totals: exampleTotals },
});

/**
 * B391 (RULING, Pathik 2026-08-19) — the sale line's stepper has an
 * operation. `redline-pos-sale.md` draws every line with `-` / count / `+`,
 * and until now the marks named nothing: `+` was another `cart.add` (which
 * mints a second row, see above) and `-` below quantity 1 had no operation
 * at all. The stepper's two marks are `set_quantity(q +/- 1)`, with `-` at
 * quantity 1 remaining `cart.remove`.
 *
 * ZERO is refused at the wire, deliberately: emptying a line is
 * `cart.remove` — a different act, with its own name and its own response.
 * A stepper that silently deleted a three-ticket line when the operator
 * pressed `-` once too often is the defect this operation exists to avoid,
 * so the refusal lives in the schema rather than in each caller's nerve.
 */
export const cartSetQuantity = writeOp({
  name: "cart.set_quantity",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { cartId: zId, lineId: zId, quantity: z.number().int().positive() },
  response: z.object({ cartId: zId, lineId: zId, totals: zTotals }).strict(),
  exampleRequest: { cartId: "cart-1", lineId: "line-1", quantity: 3 },
  exampleResponse: { cartId: "cart-1", lineId: "line-1", totals: exampleTotals },
});

const zCartLine = z
  .object({
    lineId: zId,
    productId: zId,
    variantId: zId,
    slotId: zId.optional(),
    /** B170 (§7): the departure this line rides, when it rides one — the buyer verifies WHICH crossing. */
    slotStart: zEpochMs.optional(),
    quantity: z.number().int().positive(),
    /** B170 (§10.1): what the line costs before promotions — quantity × unit, post-policy. */
    amount: zNonNegMoney,
  })
  .strict();

/** B20b(c): the cart is readable — the storefront reloads what it built (§7). */
export const cartGet = readOp({
  name: "cart.get",
  owner: "customer",
  permission: "order:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { cartId: zId },
  response: z
    .object({ cartId: zId, status: z.enum(["OPEN", "CHECKED_OUT"]), lines: z.array(zCartLine), totals: zTotals })
    .strict(),
  exampleRequest: { cartId: "cart-1" },
  exampleResponse: {
    cartId: "cart-1",
    status: "OPEN",
    lines: [{ lineId: "line-1", productId: "p-1", variantId: "adult", quantity: 2, amount: { amount: 6300, currency: "USD" } }],
    totals: exampleTotals,
  },
});

export const cartApplyPromo = writeOp({
  name: "cart.apply_promo",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { cartId: zId, code: z.string().min(1) },
  response: z.object({ cartId: zId, applied: z.boolean(), totals: zTotals }).strict(),
  exampleRequest: { cartId: "cart-1", code: "SUMMER10" },
  exampleResponse: { cartId: "cart-1", applied: true, totals: exampleTotals },
});

/**
 * The big one (§11): atomic holds, totals frozen, intent created.
 * B20b(h): buyerEmail feeds ensureGuestCustomer (§4.1 guest checkout) —
 * the order gets a customer to deliver tickets to.
 *
 * **B395 (RULING Pathik, 2026-08-19): this call is the MINTING MOMENT, and
 * nothing before it creates an order.** Everything upstream — `cart.create`,
 * `cart.add`, `cart.set_quantity`, `cart.remove`, `cart.apply_promo` — moves
 * a CART, which is pre-order state. Until `cart.checkout` returns, no order
 * record exists, so an abandoned sale has nothing to void, writes nothing to
 * the ledger, and leaves no row for a report to explain. Canceling a
 * half-built sale is a no-op by construction rather than by policy.
 *
 * It is stated here because a surface has to know it: the till's one-card
 * SALE face (`redline-pos-sale.md`) draws the tender controls beside the
 * lines, and those controls are a FACE OVER A CART — they are not an order
 * being paid. A surface that assumed otherwise would show an operator an
 * order that does not exist, and would owe a void for a sale that never
 * happened.
 *
 * Moving this moment is a §16.2 change to the order state machine and needs
 * Pathik, not a session. Walled in `packages/handlers/test/commerce.test.ts`
 * ("B395 — the minting moment"), which builds a busy cart, abandons it, and
 * proves the order list and the order store both stay empty.
 */
export const cartCheckout = writeOp({
  name: "cart.checkout",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: {
    cartId: zId,
    buyerEmail: z.string().email().optional(),
    /** B415 — the booking's name (73:2 amendment): required BY THE STOREFRONT
     * in words, optional ON THE WIRE — a POS counter sale has no buyer to
     * name, and a required field here would refuse every walk-up ticket.
     * Stored on the §4.1 customer row at creation; spoken by booking/order
     * reads behind the B20b wall (B407's answer at the source). */
    buyerName: z.string().min(1).max(200).optional(),
    /** B415 — optional, for service updates (a canceled sailing must reach
     * boarding passengers); never required, absence is silent. */
    buyerPhone: z.string().min(3).max(40).optional(),
    /** B594 (2) — the storefront shell the checkout happened in (the funnel
     * marks' own vocabulary), stamped on the order for paid-by-surface;
     * observational, absent from a client that names none. */
    surface: zFunnelSurface.optional(),
    /** B615 leg 2 — the buyer ticked the pay step's acknowledgement line
     * (Pay mints here in the classic walk). Only `true` is a tick; the server
     * records the line IT composes from the order's products. Absent is not
     * refused — the till and the native storefronts send none. */
    acknowledged: z.literal(true).optional(),
    /** B616 — the pay step's "Email me about upcoming shows" (unticked by
     * default): only `true` is a tick; with the buyer's email it joins the
     * tenant's Insider list as a checkout consent. */
    subscribe: z.literal(true).optional(),
  },
  response: z
    .object({
      orderId: zId,
      state: z.literal("PENDING_PAYMENT"),
      totals: zTotals,
      holdExpiresAt: zEpochMs,
      /** B545 (f) — `false` when the order froze NO holds (capacity-NONE
       * products): nothing lapses, so `holdExpiresAt` carries no deadline
       * and a client draws no timer. Absent on a held order (unchanged wire). */
      held: z.literal(false).optional(),
    })
    .strict(),
  exampleRequest: { cartId: "cart-1" },
  exampleResponse: { orderId: "o-1", state: "PENDING_PAYMENT", totals: exampleTotals, holdExpiresAt: 1_780_000_600_000 },
});

/**
 * B20b(f) ruling 2026-07-14: order reads name their lines — refund.request
 * consumes lineIds that no read returned before this amendment (B32's
 * escalation). `paid` is the line's frozen net from the checkout quote
 * (Inv 11: never recomputed). Lines are REQUIRED on order.get (the record
 * holds the frozen facts) and OPTIONAL on order.list/order.search (B27
 * projection rows predate the amendment and stay line-less).
 */
const zOrderLine = z
  .object({
    lineId: zId,
    productId: zId,
    /** B145 — the wire names the product (B124(b) serviceName precedent):
     * resolved read-time from the catalog store; a vanished product stays
     * honestly name-less rather than inventing one. */
    productName: z.string().min(1).optional(),
    variantId: zId.optional(),
    /** B155 — the wire names the variant too (§4.2 display name), resolved
     * off the same product row as productName; a variant the product no
     * longer names stays honestly name-less. */
    variantName: z.string().min(1).optional(),
    slotId: zId.optional(),
    /** B155 — the slot's own local occurrence (§4.3 "YYYY-MM-DD"/"HH:MM"),
     * resolved read-time from the slot store of record so operator labels
     * can speak a time instead of a slotId; a vanished slot stays honestly
     * time-less. Both ride together or not at all. */
    slotDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    slotTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
    /** B387 leg (c) — WHERE this line happened: the slot's service's own
     * location, or for a routed leg (B56) the stop the passenger BOARDS at.
     * Resolved read-time on the B145/B155 precedent, off the services store.
     * Absent wherever the chain breaks — a vanished service, a stop with no
     * location, an unwired store — and NEVER the service's home pier as a
     * fallback: the live map places a pulse only where a read puts it, and a
     * pulse at the wrong pier is indistinguishable from a measured one. */
    locationId: zId.optional(),
    quantity: z.number().int().positive(),
    paid: zNonNegMoney,
  })
  .strict();
// B107(b) — §11 order column: summaries may carry the purchase moment
// (B74 booking.list's createdAt precedent) so operator views can show a
// date; rows predating the amendment stay valid. Buyer PII deliberately
// stays OFF these wide-audience reads (the B20b buyer wall is
// booking.list's — MANAGER/ADMIN only).
// B115 — the human confirmation code (`HF7K-2Q9M`): 8 glyphs, Crockford-style
// alphabet with the ambiguous glyphs gone (no I/L/O/U/0/1), grouped for
// reading. OPTIONAL everywhere: pre-B115 orders and unwired stages answer
// honestly by id alone. The code is NEVER a lookup key on its own — the
// B115 lookup door (when it lands) demands code + buyer email.
const zConfirmationCode = z.string().regex(/^[2-9A-HJKMNP-TV-Z]{4}-[2-9A-HJKMNP-TV-Z]{4}$/);
// B350(a) — coarse purchase origin: closed source set, nothing finer than
// city, OPTIONAL everywhere (pre-B350 orders and geo-less payloads answer
// honestly without one). A heuristics/map feed, never a fraud verdict.
const zOrderOrigin = z
  .object({
    source: z.enum(["billing", "terminal", "viewer"]), // B350(b): the trusted edge's viewer stamp joined 2026-09-21
    country: z.string().min(1).optional(),
    region: z.string().min(1).optional(),
    city: z.string().min(1).optional(),
  })
  .strict();
const zOrderSummary = z
  .object({
    orderId: zId,
    state: z.string().min(1),
    total: zMoney,
    lines: z.array(zOrderLine).optional(),
    createdAt: zEpochMs.optional(),
    confirmationCode: zConfirmationCode.optional(),
    origin: zOrderOrigin.optional(),
  })
  .strict();
// B500 — the observational checkout-velocity flag (ruled 2026-09-23):
// which window counter ran hot and how hot. A FLAG for staff eyes, never
// a verdict — nothing in the platform refuses or holds on it. OPTIONAL
// everywhere: quiet counters and unwired stages answer without one.
const zVelocityFlag = z
  .object({
    dimension: z.enum(["source", "buyer"]),
    count: z.number().int().positive(),
    windowMs: z.number().int().positive(),
  })
  .strict();
// B406 (b) — the PII-clean payment summary: the tender that took the money,
// plus brand/last4 where the webhook's VERIFIED payload named them. The core
// still never sees card data (§10.4) — last4 is a receipt fact, not a PAN.
const zPaymentSummary = z
  .object({
    tender: z.string().min(1),
    brand: z.string().min(1).optional(),
    last4: z.string().regex(/^[0-9]{4}$/).optional(),
  })
  .strict();
const zOrderDetail = z
  .object({
    orderId: zId,
    state: z.string().min(1),
    total: zMoney,
    lines: z.array(zOrderLine),
    confirmationCode: zConfirmationCode.optional(),
    origin: zOrderOrigin.optional(),
    /** B406 (c) — the detail's own fact line: when the order happened and
     * through which channel, so the screen stops borrowing the roster row
     * and deep links stop dropping clauses. Optional: pre-B406 bundles keep
     * parsing (B168 additive evolution); the handler answers createdAt
     * always. */
    createdAt: zEpochMs.optional(),
    channelId: zId.optional(),
    /** B545 (e) — a PENDING_PAYMENT order's hold deadline (the last hold's
     * TTL): a reopened booking sheet resumes at Pay with the strip counting.
     * Absent when settled or hold-free — nothing lapses. */
    holdExpiresAt: zEpochMs.optional(),
    /** B406 (c) — buyer email, MANAGER/ADMIN answers ONLY (the B20b buyer
     * wall is booking.list's; every wider principal gets the same shape
     * without this field — walled in the handler's tests). */
    buyerEmail: z.string().min(1).optional(),
    /** B415 — the collected booking name and service-updates phone, the same
     * B20b MANAGER/ADMIN wall as buyerEmail; absent where checkout never
     * collected them (honest face, never a placeholder). */
    buyerName: z.string().min(1).optional(),
    buyerPhone: z.string().min(1).optional(),
    /** B406 (b) — how the money was taken, when a read can honestly say. */
    payment: zPaymentSummary.optional(),
    /** B500 — the observational velocity flags, the SAME MANAGER/ADMIN wall
     * as buyerEmail (an ops signal is not for the buyer's own screen). */
    velocity: z.array(zVelocityFlag).optional(),
  })
  .strict();
const exampleLine = { lineId: "line-1", productId: "p-1", productName: "Ferry crossing", variantId: "adult", quantity: 2, paid: { amount: 5670, currency: "USD" } };
const exampleOrder = { orderId: "o-1", state: "CONFIRMED", total: { amount: 5670, currency: "USD" } };

/**
 * B115(a) — the guest LOOKUP DOOR. A buyer who lost their session or email
 * quotes the human confirmation code + THEIR OWN buyer email; the pair is
 * the credential. The code alone is NEVER a key (it is short, therefore
 * guessable): the handler rate-limits per source and per (tenant, code)
 * and answers the SAME absent shape for every miss — unknown code, wrong
 * email, foreign tenant are indistinguishable (Inv 10; the B25c/d fence
 * stance carried to the public door).
 */
export const orderLookup = publicOp({
  name: "order.lookup",
  permission: "public",
  fields: {
    /** As the human typed it — case, spaces and hyphens are forgiven. */
    code: z.string().min(1).max(64),
    /** The buyer email the order was placed under — the code's other half. */
    email: z.string().min(3).max(320),
  },
  response: z.object({ order: zOrderDetail.optional() }).strict(),
  exampleRequest: { code: "HF7K-2Q9M", email: "buyer@example.com" },
  exampleResponse: { order: { ...exampleOrder, lines: [exampleLine], confirmationCode: "HF7K-2Q9M" } },
});

/**
 * B135 (RULED via Slack, Pathik 2026-09-21: "APPROVED for STAFF: staff may
 * resolve a confirmation code without buyer email") — the STAFF code
 * resolve behind the authenticated door. The B115(a) stance is untouched
 * for the GUEST door: a code alone is never a public key. A staff token is
 * different in kind — authenticated, tenant-fenced, and already carrying
 * order.search/order.list over every order in its tenant — so this read
 * exposes nothing the principal cannot already reach; it only lets the
 * detail's ADDRESS carry the human code instead of the machine id.
 * Uniform miss: malformed, unknown and foreign codes are the same {}.
 */
export const orderResolve = readOp({
  name: "order.resolve",
  permission: "order:read",
  principals: ["STAFF"],
  fields: { code: z.string().min(1).max(64) },
  response: z.object({ orderId: zId.optional() }).strict(),
  exampleRequest: { code: "HF7K-2Q9M" },
  exampleResponse: { orderId: "o-1" },
});

/**
 * B449 (RULED via Slack, Pathik 2026-09-21) — wallet recovery's token
 * half: the same (code, email) credential as order.lookup, the same
 * per-source + per-(tenant, code) rate walls (ONE shared attempt log),
 * the same indistinguishable miss — answering the order's signed ttk1
 * tokens, which B28's email already delivers to exactly this credential
 * holder. Condition verified at ship (recorded on the item): 30^8 code
 * space × 20-per-code/15min lockout × the email match. VOIDED
 * entitlements never leave.
 */
export const orderTokens = publicOp({
  name: "order.tokens",
  permission: "public",
  fields: {
    code: z.string().min(1).max(64),
    email: z.string().min(3).max(320),
  },
  response: z
    .object({
      order: z
        .object({
          orderId: zId,
          tokens: z.array(z.object({ entitlementId: zId, token: z.string().min(1) }).strict()),
        })
        .strict()
        .optional(),
    })
    .strict(),
  exampleRequest: { code: "HF7K-2Q9M", email: "buyer@example.com" },
  exampleResponse: { order: { orderId: "o-1", tokens: [{ entitlementId: "ent-1", token: "ttk1.abc.k1.sig" }] } },
});

export const orderGet = readOp({
  name: "order.get",
  owner: "customer",
  permission: "order:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { orderId: zId },
  response: z.object({ order: zOrderDetail.optional() }).strict(),
  exampleRequest: { orderId: "o-1" },
  exampleResponse: { order: { ...exampleOrder, lines: [exampleLine] } },
});

/**
 * B634 (b) — the booking timeline: every fact about ONE order, newest first,
 * with who did it where a store records it (frame 564:2's Card/Timeline,
 * 565:2). Read from the stores that hold each fact (orders, payments, the
 * delivery log, redemptions, refunds); a row whose store keeps no actor
 * carries none. The books seat (MANAGER/ADMIN) — the row names staff.
 */
const zBookingEventKind = z.enum([
  "created",
  "paid",
  "email_sent",
  "email_resent",
  "email_bounced",
  "email_complained",
  "scanned",
  "scan_undone",
  "scan_refused",
  "refund_requested",
  "refund_withdrawn",
  "refund_sent",
  "note",
]);
export const orderTimeline = readOp({
  name: "order.timeline",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { orderId: zId },
  response: z
    .object({
      /** Absent when the order is not this tenant's (the uniform miss). */
      rows: z
        .array(
          z
            .object({
              at: zEpochMs,
              kind: zBookingEventKind,
              actor: z
                .object({
                  kind: z.enum(["BUYER", "STAFF", "SYSTEM", "PROVIDER"]),
                  id: z.string().min(1).optional(),
                  name: z.string().min(1).optional(),
                  role: z.string().min(1).optional(),
                })
                .strict()
                .optional(),
              entitlementIds: z.array(zId).optional(),
              amount: zMoney.optional(),
              destination: z.string().min(1).optional(),
              deviceId: zId.optional(),
              refundId: zId.optional(),
              detail: z.string().min(1).optional(),
            })
            .strict(),
        )
        .optional(),
    })
    .strict(),
  exampleRequest: { orderId: "o-1" },
  exampleResponse: {
    rows: [
      { at: 1_780_000_060_000, kind: "email_sent", actor: { kind: "SYSTEM" }, entitlementIds: ["e-1"], destination: "ada@example.com" },
      { at: 1_780_000_000_000, kind: "created", actor: { kind: "BUYER", id: "guest-1" } },
    ],
  },
});

/**
 * B633 — a staff NOTE on a booking (frame 564:2's ADD A NOTE door): the words
 * land on the booking-event stream with the staff actor and read back on
 * order.timeline as a "note" row. Append-only — nobody edits or deletes a
 * note, the timeline's own rule. The books seat (MANAGER/ADMIN), the same
 * seat that reads the timeline; another tenant's order is the uniform 404.
 * Not the order state machine and not the ledger — a note moves no money.
 */
export const NOTE_MAX_CHARS = 1_000;
export const orderNoteAdd = writeOp({
  name: "order.note.add",
  permission: "order:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  /* a note with nothing but spaces is refused here; the handler stores it trimmed (the MCP membrane does not translate .trim()) */
  fields: { orderId: zId, text: z.string().min(1).max(NOTE_MAX_CHARS).regex(/\S/) },
  response: z.object({ orderId: zId, at: zEpochMs }).strict(),
  exampleRequest: { orderId: "o-1", text: "Comped her a drink at the bar for the trouble" },
  exampleResponse: { orderId: "o-1", at: 1_780_000_000_000 },
});

/**
 * B522 -- the buyer's own "email me my tickets": re-delivery only (Sec11,
 * the same no-state-change stance as entitlement.resend), fanned out over
 * every entitlement the order carries, to an explicit destination the
 * buyer types. CUSTOMER-only and owner-fenced (B25c/d) -- this is a guest
 * session's OWN order, never a MANAGER's staff-side resend across the
 * tenant (that stays entitlement.resend). A kiosk sale checks out through
 * a minted guest session exactly like the storefront (session.guest,
 * B20b(h)), so no new principal widening is needed for the kiosk's Done
 * face to call this with its own just-completed order.
 */
export const orderEmail = writeOp({
  name: "order.email",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER"],
  /* B608 — same bound as entitlement.resend: mail travels to an email. The
     kiosk pre-checks its own entry (emailLooksRight); nobody else types one. */
  fields: { orderId: zId, destination: zRecipient },
  response: z.object({ orderId: zId, queued: z.number().int().min(0) }).strict(),
  exampleRequest: { orderId: "o-1", destination: "guest@example.com" },
  exampleResponse: { orderId: "o-1", queued: 1 },
});

/**
 * B166 leg (B) — the list's bounded page. `limit`/`newestFirst`/`cursor`
 * ride the UNFILTERED staff list as a true bounded store read (a page
 * costs one tiny index Query + one BatchGet wave however big the book
 * grows); a cursor is only ever EMITTED there. With filters or a customer
 * fence, `newestFirst` reverses and `limit` caps the filtered answer —
 * bounded wire, same universe. A bare request stays return-everything in
 * creation order, exactly as before (pinned — the admin's aggregate
 * readers depend on it until they move to pages).
 */
export const orderList = readOp({
  name: "order.list",
  owner: "customer",
  permission: "order:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: {
    customerId: zId.optional(),
    slotId: zId.optional(),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(500).optional(),
    newestFirst: z.boolean().optional(),
  },
  response: z.object({ orders: z.array(zOrderSummary), cursor: z.string().optional() }).strict(),
  exampleRequest: { customerId: "c-1" },
  exampleResponse: { orders: [exampleOrder] },
});

export const orderSearch = readOp({
  name: "order.search",
  owner: "customer",
  permission: "order:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { query: z.string().min(1), cursor: z.string().optional() },
  response: z.object({ orders: z.array(zOrderSummary), cursor: z.string().optional() }).strict(),
  exampleRequest: { query: "smith" },
  exampleResponse: { orders: [exampleOrder] },
});

/**
 * B74 R2 (docs/OPERATIONS.md §7; §4.6 service day, §11) — the back-office
 * bookings read ("who's coming Saturday"): orders joined with the party
 * checkout FROZE (Inv 11 — never recomputed), the §4.6 service days their
 * lines' slots name — with fully-untimed bookings falling back to the
 * purchase UTC day and DECLARING it (serviceDayBasis: PURCHASE) — buyer
 * email, promo codes as entered, cumulative refunds (the Inv 8 baseline)
 * and the redemption log's scan state. MANAGER/ADMIN only: buyer PII,
 * party and scan state are back-office truth — the B20b buyer wall stays
 * absolute. Cursor pages walk (createdAt, orderId); the day range is
 * inclusive on both bounds.
 */
const zServiceDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const bookingList = readOp({
  name: "booking.list",
  permission: "order:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    from: zServiceDay,
    to: zServiceDay,
    productId: zId.optional(),
    slotId: zId.optional(),
    channelId: zId.optional(),
    status: z.string().min(1).optional(),
    promoCode: z.string().min(1).optional(),
    q: z.string().min(1).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(500).optional(),
  },
  response: z
    .object({
      bookings: z.array(
        z
          .object({
            orderId: zId,
            state: z.string().min(1),
            createdAt: zEpochMs,
            confirmationCode: zConfirmationCode.optional(),
            channelId: zId.optional(),
            customerId: zId.optional(),
            buyerEmail: z.string().min(1).optional(),
            /** B415/B407 — the GUEST cell prefers the collected name over the
             * email; same wall, same audience, honestly absent otherwise. */
            buyerName: z.string().min(1).optional(),
            total: zMoney,
            refunded: zNonNegMoney.optional(),
            party: z.array(
              z
                .object({
                  productId: zId,
                  /** B174 — the wire names the product (B145's rule, this read's
                   * turn): resolved read-time off the store of record; a vanished
                   * product stays honestly name-less, never an id on a face. */
                  productName: z.string().min(1).optional(),
                  variantId: zId,
                  /** B174 — the variant's §4.2 display name rides the same read. */
                  variantName: z.string().min(1).optional(),
                  quantity: z.number().int().positive(),
                })
                .strict(),
            ),
            serviceDays: z.array(zServiceDay).min(1),
            serviceDayBasis: z.enum(["SLOT", "PURCHASE"]),
            slotIds: z.array(zId).min(1).optional(),
            /** B403 (56:2) — the row's DEPARTURES: each bound slot's §4.3
             * local occurrence, resolved off the slot store the day
             * derivation already reads (B155's pattern, this read's turn),
             * so the roster can say "Fri 10:30" instead of borrowing a day
             * word. Ordered by (date, time); absent for untimed bookings and
             * rows whose slots have vanished — never invented. */
            departures: z
              .array(z.object({ slotId: zId, date: zServiceDay, time: z.string().regex(/^\d{2}:\d{2}$/) }).strict())
              .min(1)
              .optional(),
            promoCodes: z.array(z.string().min(1)).min(1).optional(),
            entitlements: z
              .object({ issued: z.number().int().nonnegative(), scanned: z.number().int().nonnegative() })
              .strict(),
          })
          .strict(),
      ),
      cursor: z.string().min(1).optional(),
    })
    .strict(),
  exampleRequest: { from: "2026-03-01", to: "2026-03-31" },
  exampleResponse: {
    bookings: [
      {
        orderId: "o-1",
        state: "CONFIRMED",
        createdAt: 1_780_000_000_000,
        channelId: "web",
        customerId: "c-1",
        buyerEmail: "alice@example.com",
        buyerName: "Maya Chen",
        total: { amount: 6500, currency: "USD" },
        party: [
          { productId: "sail", variantId: "adult", quantity: 2 },
          { productId: "sail", variantId: "bike", quantity: 1 },
        ],
        serviceDays: ["2026-03-02"],
        serviceDayBasis: "SLOT",
        slotIds: ["sched-1#2026-03-02T09:00"],
        departures: [{ slotId: "sched-1#2026-03-02T09:00", date: "2026-03-02", time: "09:00" }],
        promoCodes: ["SUMMER"],
        entitlements: { issued: 3, scanned: 1 },
      },
    ],
  },
});

export const orderCancel = writeOp({
  name: "order.cancel",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { orderId: zId, reason: z.string().min(1).optional() },
  response: z.object({ orderId: zId, state: z.string().min(1) }).strict(),
  exampleRequest: { orderId: "o-1" },
  exampleResponse: { orderId: "o-1", state: "CANCELLED" },
});

/**
 * B415 — CLEAR CART's wire half: the buyer walks away from a PENDING_PAYMENT
 * order ON PURPOSE and the seats free NOW rather than when the hold TTL dies
 * (Pathik 2026-08-26: "an abandoned cart must not strand seats until the
 * timer dies"). This drives the COMMITTED §5.1 `expire` transition early —
 * the same edge B29's sweeper walks on the clock; the transition table is
 * untouched and PENDING_PAYMENT predates money, so no ledger row exists to
 * touch. A SUCCEEDED intent refuses: money moved, the webhook owns the
 * order (§7). B25d: a foreign order answers the uniform absent shape.
 */
/**
 * B545 (d) — the buyer attaches at PAY (Pathik 2026-09-24, RULED: "move that
 * portion to payment step" — the wallet sits above the fields). The Cart
 * step's Continue is cart.checkout WITHOUT a buyer; this names the buyer on
 * the PENDING_PAYMENT order — from the wallet's billing details or the typed
 * fields — before the confirm. A field write only: no transition, no ledger
 * row (the §5.1 table is untouched). Owner-fenced like order.abandon (B25d);
 * a CONFIRMED order refuses — the buyer is fixed once the money moved. A
 * corrected email before paying re-binds (create-or-reuse by email, §4.1);
 * a cart that carried an account customer keeps it.
 */
export const orderBuyerSet = writeOp({
  name: "order.buyer.set",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: {
    orderId: zId,
    buyerEmail: z.string().email(),
    buyerName: z.string().min(1).max(200).optional(),
    buyerPhone: z.string().min(3).max(40).optional(),
    /** B615 leg 2 — the acknowledgement tick, when Cart's Continue already minted (see cart.checkout). */
    acknowledged: z.literal(true).optional(),
    /** B616 — the Insider opt-in, when Cart's Continue already minted (see cart.checkout). */
    subscribe: z.literal(true).optional(),
  },
  response: z.object({ orderId: zId, state: z.literal("PENDING_PAYMENT") }).strict(),
  exampleRequest: { orderId: "o-1", buyerEmail: "guest@example.com", buyerName: "Ada Lovelace" },
  exampleResponse: { orderId: "o-1", state: "PENDING_PAYMENT" },
});

export const orderAbandon = writeOp({
  name: "order.abandon",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { orderId: zId },
  response: z.object({ orderId: zId, state: z.literal("EXPIRED") }).strict(),
  exampleRequest: { orderId: "o-1" },
  exampleResponse: { orderId: "o-1", state: "EXPIRED" },
});

export const refundRequest = writeOp({
  name: "refund.request",
  permission: "refund:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    orderId: zId,
    // B468 (e), Pathik ruling 2026-09-15: "refund 1 of 2 adults" is a COUNT.
    // `quantity` states how many of the line's tickets void (oldest
    // unredeemed first, the execute-time pick) beside the money amount —
    // WHICH ticket voids is chosen deterministically, never inferred from
    // a bare dollar figure (spec \u00a712 per-entitlement identity).
    lines: z.array(z.object({ lineId: zId, amount: zNonNegMoney, quantity: z.number().int().positive().optional() }).strict()).min(1),
    reason: z.string().min(1),
  },
  response: z.object({ refundId: zId, status: z.literal("REQUESTED") }).strict(),
  exampleRequest: { orderId: "o-1", lines: [{ lineId: "line-1", amount: { amount: 2835, currency: "USD" } }], reason: "weather" },
  exampleResponse: { refundId: "ref-1", status: "REQUESTED" },
});

/**
 * B512 (RULED 2026-09-23, Pathik): take back a mis-pressed refund request.
 * REQUESTED → WITHDRAWN only; refused once processing has begun (execute
 * stamps before any money moves) and refused for a refund already paid
 * back. No journal, no void, no provider call — the ledger and the order's
 * machine never saw a REQUESTED refund. Same seat as request/execute; the
 * authorizer's attempt trail is its audit row.
 */
export const refundWithdraw = writeOp({
  name: "refund.withdraw",
  permission: "refund:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { refundId: zId },
  response: z.object({ refundId: zId, status: z.literal("WITHDRAWN") }).strict(),
  exampleRequest: { refundId: "ref-1" },
  exampleResponse: { refundId: "ref-1", status: "WITHDRAWN" },
});

export const refundExecute = writeOp({
  name: "refund.execute",
  permission: "refund:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { refundId: zId },
  response: z.object({ refundId: zId, status: z.literal("EXECUTED"), refunded: zNonNegMoney }).strict(),
  exampleRequest: { refundId: "ref-1" },
  exampleResponse: { refundId: "ref-1", status: "EXECUTED", refunded: { amount: 2835, currency: "USD" } },
});

/**
 * B81 (filed from B69(a2)'s vocabulary check) — the pending-refunds read:
 * no other op answers "refunds awaiting execution" tenant-wide. Served
 * LIVE from the refund store of record (the B74 R1/R2 rationale — refunds
 * carry no §12 feed at this granularity; a projection would be stale
 * exactly while a refund sits REQUESTED). Rows walk (requestedAt,
 * refundId) behind the house versioned cursor; the window is inclusive on
 * requestedAt and an inverted one refuses by name. `requested` is the
 * per-line sum staff approved; `executed` is refund.execute's stamped
 * outcome — authoritative, never recomputed (Inv 8 lives in the write
 * path alone). MANAGER/ADMIN via ledger:read — refund state is books
 * truth; the CAPABILITY-opened set stays the four report ops (pinned).
 */
const zRefundRow = z
  .object({
    refundId: zId,
    orderId: zId,
    status: z.enum(["REQUESTED", "EXECUTED", "WITHDRAWN"]),
    requestedAt: zEpochMs,
    executedAt: zEpochMs.optional(),
    /** B512 — when a request was withdrawn (status WITHDRAWN). */
    withdrawnAt: zEpochMs.optional(),
    reason: z.string().min(1),
    requested: zNonNegMoney,
    executed: zNonNegMoney.optional(),
    /** B406 (d) — the order's human code (B115), resolved read-time from the
     * order record so the refunds queue can draw the frame's code chip;
     * absent for pre-B115 orders and unwired stages. */
    confirmationCode: zConfirmationCode.optional(),
  })
  .strict();
export const refundList = readOp({
  name: "refund.list",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    status: z.enum(["REQUESTED", "EXECUTED", "WITHDRAWN"]).optional(),
    from: zEpochMs.optional(),
    to: zEpochMs.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(500).optional(),
  },
  response: z.object({ refunds: z.array(zRefundRow), cursor: z.string().min(1).optional() }).strict(),
  exampleRequest: { status: "REQUESTED" },
  exampleResponse: {
    refunds: [
      {
        refundId: "ref-1",
        orderId: "o-1",
        status: "REQUESTED",
        requestedAt: 1_780_000_000_000,
        reason: "weather",
        requested: { amount: 2835, currency: "USD" },
      },
    ],
  },
});

/**
 * B20b(d) ruling 2026-07-14: the response carries the provider's client
 * secret — §7 step 4, elements cannot mount without it. Like device.enroll's
 * credential secret: delivered at creation, never readable afterwards (an
 * Inv-4 replay of the same clientKey re-delivers the same outcome).
 * publishableKey arrives when the channel's config carries one (B20b(g));
 * honest absence until then.
 */
export const paymentIntentCreate = writeOp({
  name: "payment.intent.create",
  owner: "customer",
  permission: "payment:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: {
    orderId: zId,
    amount: zNonNegMoney,
    provider: z.string().min(1),
    /**
     * B376 — which provider tender the intent is for: `card` (the storefront's
     * element, the default) or `terminal` (card-present on a Stripe Terminal
     * reader: `card_present`, manual capture — the handler admits STAFF only,
     * because a reader is a till's instrument, never a buyer's).
     */
    tender: z.enum(["card", "terminal"]).optional(),
  },
  response: z
    .object({
      intentId: zId,
      state: z.literal("REQUIRES_PAYMENT"),
      clientSecret: z.string().min(1),
      publishableKey: z.string().min(1).optional(),
      /** B91 GAP-6 — present when the intent lives on the tenant's CONNECTED account: Stripe.js must mount on it. */
      stripeAccount: z.string().min(1).optional(),
    })
    .strict(),
  exampleRequest: { orderId: "o-1", amount: { amount: 5670, currency: "USD" }, provider: "stripe" },
  exampleResponse: { intentId: "pi-1", state: "REQUIRES_PAYMENT", clientSecret: "pi_secret_example" },
});

/**
 * B376 — the Terminal SDK's connection token: a short-lived provider secret the
 * reader session is opened with (Stripe: `POST /v1/terminal/connection_tokens`).
 * The secret rides the response and is never stored or logged, like an
 * intent's client secret (B20b(d)).
 *
 * B522 (RULED 2026-09-23, Pathik — Richard's wire ask APPROVED): the door
 * also admits `DEVICE:KIOSK`, SCOPED to the device's own location — the
 * handler resolves the kiosk's locationId through ops-config's
 * terminalLocations map and mints a location-scoped token; an unmapped
 * location REFUSES (fail closed), so a paired kiosk can open a reader
 * session only for where it stands. STAFF tokens stay unscoped (a till's
 * instrument follows its seller).
 */
export const terminalConnectionToken = readOp({
  name: "terminal.connection_token",
  permission: "payment:write",
  principals: ["STAFF", "DEVICE:KIOSK"],
  fields: { provider: z.string().min(1) },
  response: z.object({ secret: z.string().min(1) }).strict(),
  exampleRequest: { provider: "stripe" },
  exampleResponse: { secret: "pst_test_example" },
});

/**
 * B522 (RULED 2026-09-23) — map a tenant location to the payment provider's
 * Terminal location (Stripe: tml_…). The kiosk door's scope wall has ONE
 * writer, this op (the B524 gtm precedent): ADMIN's Settings → Device fleet
 * leg speaks it; an absent providerLocationId clears the one mapping. The
 * authorizer's per-attempt trail is the audit row.
 */
export const terminalLocationSet = writeOp({
  name: "terminal.location.set",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    locationId: z.string().min(1).max(120),
    providerLocationId: z.string().min(1).max(200).optional(),
  },
  response: z
    .object({
      locationId: z.string().min(1),
      providerLocationId: z.string().min(1).optional(),
      updatedAt: zEpochMs,
    })
    .strict(),
  exampleRequest: { locationId: "loc-hall", providerLocationId: "tml_example" },
  exampleResponse: { locationId: "loc-hall", providerLocationId: "tml_example", updatedAt: 1_790_000_000_000 },
});

/**
 * B376 — capture a card-present intent the reader confirmed (Terminal intents
 * are minted with manual capture, so the till's own act settles the money —
 * Stripe's recommended flow). Idempotent on the client key; STAFF only. The
 * order confirms on the provider's `payment_intent.succeeded` event through
 * `payment.webhook.apply`, exactly as a storefront card does (Inv 14).
 */
export const paymentIntentCapture = writeOp({
  name: "payment.intent.capture",
  permission: "payment:write",
  principals: ["STAFF"],
  fields: { intentId: zId },
  response: z.object({ intentId: zId, status: z.string().min(1) }).strict(),
  exampleRequest: { intentId: "pi-o-1-1" },
  exampleResponse: { intentId: "pi-o-1-1", status: "SUCCEEDED" },
});

/** Exactly-once on the provider event id (Inv 5, 14) — never a client key. */
export const paymentWebhookApply = internalOp({
  name: "payment.webhook.apply",
  permission: "internal",
  fields: { provider: z.string().min(1), providerEventId: zId, signature: z.string().min(1), payload: z.unknown() },
  response: z.object({ applied: z.boolean(), replayed: z.boolean() }).strict(),
  exampleRequest: { provider: "stripe", providerEventId: "evt-1", signature: "sig", payload: {} },
  exampleResponse: { applied: true, replayed: false },
});

export const paymentRecordExternal = writeOp({
  name: "payment.record_external",
  permission: "payment:write",
  principals: ["STAFF"],
  fields: { orderId: zId, tender: z.enum(["CASH", "TERMINAL"]), amount: zNonNegMoney },
  response: z.object({ tenderId: zId, orderState: z.string().min(1) }).strict(),
  exampleRequest: { orderId: "o-1", tender: "CASH", amount: { amount: 5670, currency: "USD" } },
  exampleResponse: { tenderId: "tender-1", orderState: "CONFIRMED" },
});

/**
 * B175 (class-B TRA-211, defaulted after 48h on 2026-08-10; Pathik may
 * overrule — reversal: drop the two optional fields): each summary row
 * ALSO answers `moved` (the positive-leg sum, well-defined under Inv 1's
 * balance to zero) and `reference` (the order id off the entries' refs),
 * because a books row with no amount and no reference is nothing an
 * accountant could use. The B25 stance is narrowed, not retired:
 * per-entry accounts, signed legs and refs stay the accounting module's
 * view and never cross this surface. Audience unchanged — report.run
 * already answers aggregates of the same figures to the same seat.
 */
export const ledgerRead = readOp({
  name: "ledger.read",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { from: zEpochMs.optional(), to: zEpochMs.optional(), kind: z.string().min(1).optional() },
  response: z
    .object({
      journals: z.array(
        z
          .object({ id: zId, kind: z.string().min(1), postedAt: zEpochMs, moved: zNonNegMoney.optional(), reference: zId.optional() })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: { journals: [{ id: "j-1", kind: "sale", postedAt: 1_780_000_000_000, moved: { amount: 5_670, currency: "USD" }, reference: "o-1" }] },
});

export const reportRun = readOp({
  name: "report.run",
  permission: "ledger:read",
  // B42(a): CAPABILITY opened deliberately — the B44 thin slice's report
  // step. The frozen grant set must still hold ledger:read per run.
  principals: ["STAFF:MANAGER", "STAFF:ADMIN", "CAPABILITY"],
  // B49: `spend` — AI run costs (B48 flight records) per tenant/feature.
  // report.deliver deliberately does NOT grow it: no spend renderer exists.
  fields: { report: z.enum(["sales", "reconciliation", "occupancy", "spend"]), from: zEpochMs, to: zEpochMs },
  response: z.object({ rows: z.array(z.unknown()) }).strict(),
  exampleRequest: { report: "sales", from: 1_780_000_000_000, to: 1_780_086_400_000 },
  exampleResponse: { rows: [] },
});

/**
 * B44(c) — report.deliver: run a report AND email it through the stage's
 * delivery seam. The thin-slice gate's step ("email me a weekly sales
 * report" actually arriving). CAPABILITY opened deliberately so the
 * composer may plan it; the frozen grant set must carry report:deliver
 * per run (B41's no-escalation wall governs authoring).
 */
export const reportDeliver = writeOp({
  name: "report.deliver",
  permission: "report:deliver",
  principals: ["STAFF:ADMIN", "CAPABILITY"],
  fields: {
    report: z.enum(["sales", "reconciliation", "occupancy"]),
    from: zEpochMs,
    to: zEpochMs,
    recipient: z.string().email().max(320),
  },
  response: z.object({ deliveryId: zId, rowCount: z.number().int().min(0) }).strict(),
  exampleRequest: { report: "sales", from: 1_780_000_000_000, to: 1_780_086_400_000, recipient: "owner@example.com" },
  exampleResponse: { deliveryId: "rd-1", rowCount: 1 },
});

/**
 * B471 — alert.deliver: the missing alert op. B464 gave capabilities EVENT
 * triggers and B465 gave plans the event's own facts ($event bindings);
 * this op DELIVERS the operator-worded message those bindings compose
 * ("when a pool crosses its low-capacity threshold, email me a heads-up").
 * Shaped like report.deliver: same audience (ADMIN + CAPABILITY so the
 * composer may plan it; the frozen grant set must carry alert:deliver per
 * run), same email seam, same Inv 4 ride. Until this landed the composer's
 * decline for the intent class was CORRECT — the catalog had no such op.
 *
 * B469 (e) — the PILOT ENTITLEMENT: first genuinely new tenant-visible
 * feature after the customization ladder, so it rides feature.alerts.email
 * (dark for existing tenants until adoption, on from birth for new ones).
 */
export const alertDeliver = writeOp({
  name: "alert.deliver",
  permission: "alert:deliver",
  principals: ["STAFF:ADMIN", "CAPABILITY"],
  entitlement: "feature.alerts.email",
  fields: {
    recipient: z.string().email().max(320),
    subject: z.string().min(1).max(200),
    body: z.string().min(1).max(5000),
  },
  response: z.object({ deliveryId: zId }).strict(),
  exampleRequest: {
    recipient: "owner@example.com",
    subject: "Low capacity: Morning Ferry",
    body: "Pool 'Morning Ferry' is down to 3 seats for the 09:00 sailing.",
  },
  exampleResponse: { deliveryId: "ad-1" },
});

/**
 * B74 R3 (docs/OPERATIONS.md §7 R3; §11) — bucketed series off the B27
 * sales facts: trends, pacing (the client compares same-weekday offsets)
 * and channel mix (`by: channel` — R5 folds in here). Same books audience
 * as report.run (B42(a): CAPABILITY deliberately admitted, ledger:read per
 * run still frozen). Money rows are integer minor units in the facts'
 * frozen currency — one row per (bucket, currency), never a mixed sum;
 * `orders` rows are counts and carry no currency. Facts predating the
 * channel stamp bucket under "unknown" honestly. Window inclusive on both
 * edges; inverted windows are refused by name.
 */
export const reportSeries = readOp({
  name: "report.series",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN", "CAPABILITY"],
  fields: {
    metric: z.enum(["sales", "orders", "refunds", "tickets"]),
    // B355: "product" buckets over the fact's frozen per-product split.
    by: z.enum(["day", "channel", "hour", "product"]),
    from: zEpochMs,
    to: zEpochMs,
    /** B403 (38:2 spark) — optional PRODUCT scope: narrow to the facts'
     * frozen line for this product (by:"day" then answers one product's
     * daily series). Facts that cannot name it contribute nothing. */
    product: z.string().min(1).optional(),
  },
  response: z
    .object({
      rows: z.array(
        z
          .object({
            bucket: z.string().min(1),
            value: z.number().int().nonnegative(),
            currency: z.string().regex(/^[A-Z]{3}$/).optional(),
            /** B355 (by: product) — the product's display name, resolved at
             * read time through the same store order.search rides; absent
             * when the store is unwired or the row is the honest "unknown". */
            label: z.string().min(1).optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: { metric: "sales", by: "day", from: 1_780_000_000_000, to: 1_780_086_400_000 },
  exampleResponse: { rows: [{ bucket: "2026-05-28", value: 4200, currency: "USD" }] },
});

export const reportReconcile = readOp({
  name: "report.reconcile",
  permission: "ledger:read",
  // B42(a): CAPABILITY opened deliberately — see report.run.
  principals: ["STAFF:MANAGER", "STAFF:ADMIN", "CAPABILITY"],
  fields: { day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) },
  /* B648 — payoutsCompared: the Stripe payouts the day's answer compared; 0 = none arrived, so "balanced" claims nothing about one.
     findings: the same sentences, each with the order it names (when it names one) so the report can open it. */
  response: z.object({ balanced: z.boolean(), discrepancies: z.array(z.string()), findings: z.array(z.object({ text: z.string().min(1), orderId: zId.optional() }).strict()).optional(), payoutsCompared: z.number().int().nonnegative().optional() }).strict(),
  exampleRequest: { day: "2026-07-09" },
  exampleResponse: { balanced: true, discrepancies: [], payoutsCompared: 1 },
});

/**
 * B74 R7 (docs/OPERATIONS.md §7 R7) — ops.alerts: the home screen's ONE
 * read. Server-composed from the R1 occupancy truth (NEAR_SELLOUT per
 * pool, SELLING_BADLY per slot — UPCOMING slots whose §4.6 localDate lies
 * in [day, day+lookaheadDays] inclusive) and the R4 scan truth
 * (UNSCANNED_DEPARTURE — the anchor day's departed, non-CANCELLED slots).
 * `day` is the CLIENT's tenant-local today (the B73 stance); the server
 * clock decides only departed-ness. Thresholds are tenant config with
 * per-preset defaults (ferry ≠ zoo): preset picks the default set,
 * `thresholds` overrides field by field — B74 admits no new write ops,
 * so the persistent config home is its own item and this is the whole
 * config surface today. Every comparison is strict-<; ratios are integer
 * basis points. RECONCILIATION_DISCREPANCY cannot be evaluated on any
 * stage yet (provider payout ingest has no §11 operation — B30, the same
 * wall report.run(reconciliation) names), so it arrives in `skipped`
 * with the reason — never a silent absence an operator would read as
 * "books balanced". MANAGER/ADMIN only; CAPABILITY stays refused (the
 * B42(a) stance — growing the opened set is a red test by default).
 */
const zAlertDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const zBps = z.number().int().min(0).max(10_000);
const zAlertBase = {
  slotId: zId,
  scheduleId: zId,
  serviceId: zId,
  /** B124(b): the alarm names its product (display name, never an id). */
  serviceName: z.string().min(1).optional(),
  localDate: zAlertDay,
  start: zEpochMs,
};
/** B80 — the tenant's STANDING alert thresholds (docs/OPERATIONS.md §7 R7's
 * "tenant config with sensible per-preset defaults", given a home). The
 * record stores preset + field overrides exactly as resolveAlertThresholds
 * consumes them; `resolved` echoes what the tenant is ACTUALLY running
 * under and `source` says whether that came from config or the defaults. */
const zOpsThresholdOverrides = z
  .object({
    nearSelloutLeft: z.number().int().min(1).optional(),
    sellingBadlyLoadFactorBps: zBps.optional(),
    unscannedScanRateBps: zBps.optional(),
  })
  .strict();
const zOpsResolved = z
  .object({ nearSelloutLeft: z.number().int().min(1), sellingBadlyLoadFactorBps: zBps, unscannedScanRateBps: zBps })
  .strict();
// B103 (AC-4) — guest-mint caps are tenant config (§9.2: the public
// door's budget belongs to the channel's tenant, not a global constant).
const zGuestMintOverrides = z
  .object({
    maxPerChannelPerWindow: z.number().int().min(1).optional(),
    maxPerSourcePerWindow: z.number().int().min(1).optional(),
  })
  .strict();
const zGuestMintResolved = z
  .object({
    maxPerChannelPerWindow: z.number().int().min(1),
    maxPerSourcePerWindow: z.number().int().min(1),
    windowMs: z.number().int().min(1),
  })
  .strict();
const zOpsConfigBody = z
  .object({
    preset: z.enum(["ferry", "zoo", "attraction"]).optional(),
    // B126 — the tenant's type; drives every surface's vocabulary pack.
    tenantType: z.enum(["FERRY", "SHOW", "PARK", "ATTRACTION", "RETAIL"]).optional(),
    thresholds: zOpsThresholdOverrides.optional(),
    guestMint: zGuestMintOverrides.optional(),
    // B125 — step 0 identity + resume facts (additive; the domain door re-validates).
    businessName: z.string().min(1).max(120).optional(),
    homeCurrency: z.string().regex(/^[A-Z]{3}$/).optional(),
    timezone: z.string().min(1).optional(),
    // B621 — the tenant's clock. Absent = 12h (AM/PM), the ruled default;
    // "24h" is the European face. Every surface reads it; the storefront's
    // public config echoes it so the buyer face needs no second read.
    timeFormat: z.enum(["12h", "24h"]).optional(),
    embedHandedOff: z.boolean().optional(),
    rehearsalGreen: z.boolean().optional(),
    // B524 leg 2 — the tenant's GTM container (set by integrations.gtm.set only).
    gtmContainerId: z.string().regex(GTM_CONTAINER_ID).optional(),
    // B63 (e2) — where a customer's reply lands (the ticket email's Reply-To).
    contactEmail: z.string().min(1).max(320).optional(),
    // B653 (c) — where guests find the venue (553:2's foot line): one line each, ≤120.
    venueAddress: z.string().min(1).max(120).optional(),
    venuePhone: z.string().min(1).max(120).optional(),
    venueNote: z.string().min(1).max(120).optional(),
    // B522 (Device-fleet leg) — the reader-location map, READ here (its one
    // writer is terminal.location.set): tenant location → the provider's
    // Terminal location. Absent when nothing is mapped.
    terminalLocations: z.record(z.string().min(1), z.string().min(1)).optional(),
    // B411 (c), defaulted 2026-08-28: when the stored record last changed —
    // the saved-age face's one fact. Absent when nothing is stored.
    updatedAt: zEpochMs.optional(),
    resolved: zOpsResolved,
    resolvedGuestMint: zGuestMintResolved,
    source: z.enum(["defaults", "config"]),
  })
  .strict();

export const opsConfigGet = readOp({
  name: "ops.config.get",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: zOpsConfigBody,
  exampleRequest: {},
  exampleResponse: {
    resolved: { nearSelloutLeft: 10, sellingBadlyLoadFactorBps: 2000, unscannedScanRateBps: 5000 },
    resolvedGuestMint: { maxPerChannelPerWindow: 600, maxPerSourcePerWindow: 30, windowMs: 3600000 },
    source: "defaults",
  },
});

export const opsConfigSet = writeOp({
  name: "ops.config.set",
  // Tenant configuration write — the channel-config scope (ADMIN-only);
  // a dedicated ops:write scope would be an authorizer amendment (escalation).
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    preset: z.enum(["ferry", "zoo", "attraction"]).optional(),
    tenantType: z.enum(["FERRY", "SHOW", "PARK", "ATTRACTION", "RETAIL"]).optional(),
    thresholds: zOpsThresholdOverrides.optional(),
    guestMint: zGuestMintOverrides.optional(),
    businessName: z.string().min(1).max(120).optional(),
    homeCurrency: z.string().regex(/^[A-Z]{3}$/).optional(),
    timezone: z.string().min(1).optional(),
    // B621 — the tenant's clock (12h default / 24h), set from Settings → Business.
    timeFormat: z.enum(["12h", "24h"]).optional(),
    // B63 (e2) — one plain address; the domain names a refusal (contactEmail must be …).
    contactEmail: z.string().min(1).max(320).optional(),
    // B653 (c) — where guests find the venue (553:2's foot line): one line each, ≤120.
    venueAddress: z.string().min(1).max(120).optional(),
    venuePhone: z.string().min(1).max(120).optional(),
    venueNote: z.string().min(1).max(120).optional(),
    embedHandedOff: z.boolean().optional(),
    rehearsalGreen: z.boolean().optional(),
  },
  response: zOpsConfigBody,
  exampleRequest: { preset: "zoo", thresholds: { nearSelloutLeft: 3 } },
  exampleResponse: {
    preset: "zoo",
    thresholds: { nearSelloutLeft: 3 },
    resolved: { nearSelloutLeft: 3, sellingBadlyLoadFactorBps: 1000, unscannedScanRateBps: 3000 },
    resolvedGuestMint: { maxPerChannelPerWindow: 600, maxPerSourcePerWindow: 30, windowMs: 3600000 },
    source: "config",
  },
});

/**
 * B524 leg 2 — Settings → Integrations → Google Tag Manager (RULED
 * 2026-09-23, Pathik: "gtm id will be provided by client - we should have
 * a setup in Settings"). Its OWN door, so the audit trail names the act on
 * set AND on clear (`containerId` ABSENT clears — the MCP membrane
 * translates no nullable, and absence is the honest "none"). ADMIN-only on the tenant-config
 * scope (ops.config.set's precedent) — the container's tags run on the
 * hosted checkout page, so this is the owner's call, not a manager's.
 */
export const integrationsGtmSet = writeOp({
  name: "integrations.gtm.set",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { containerId: z.string().regex(GTM_CONTAINER_ID).optional() },
  response: z.object({ gtmContainerId: z.string().regex(GTM_CONTAINER_ID).optional(), updatedAt: zEpochMs }).strict(),
  exampleRequest: { containerId: "GTM-AB12CD" },
  exampleResponse: { gtmContainerId: "GTM-AB12CD", updatedAt: 1_790_000_000_000 },
});

/**
 * B524 leg 2 — the "Test" affordance: asks Google whether this container
 * is published (`gtm.js?id=` answers 200 for a live container, 404 for an
 * unknown or unpublished one). A read — nothing is stored — and ADMIN-only
 * like the write it tests. `reachable: false` with a status is a finding,
 * not a refusal; a stage with no egress port refuses by name.
 */
export const integrationsGtmCheck = readOp({
  name: "integrations.gtm.check",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { containerId: z.string().regex(GTM_CONTAINER_ID) },
  response: z
    .object({
      containerId: z.string().regex(GTM_CONTAINER_ID),
      published: z.boolean(),
      /** Google's HTTP status for gtm.js, when it answered at all. */
      status: z.number().int().optional(),
    })
    .strict(),
  exampleRequest: { containerId: "GTM-AB12CD" },
  exampleResponse: { containerId: "GTM-AB12CD", published: true, status: 200 },
});

/**
 * B606 (f) — Settings → Emails (RULED 2026-10-01, Pathik: "this email should
 * be customizable per tenant via backoffice"; frame 547:2). What the tenant
 * shapes in its ticket email — each field optional, the platform default
 * standing where it is absent; the layout, the ticket cards, the QR, the
 * order summary and the platform's two lines are not the tenant's. Merged
 * onto the ops-config record by its own audited door (integrations.gtm.set's
 * shape: ADMIN on the tenant-config scope; every field ABSENT clears). The
 * domain re-validates and names each refusal by field.
 */
const zTicketEmailSettingsFields = {
  senderName: z.string().min(1).max(160).optional(),
  subject: z.string().min(1).max(320).optional(),
  opening: z.string().min(1).max(560).optional(),
  goodToKnow: z.string().min(1).max(1200).optional(),
  contactPhone: z.string().min(1).max(80).optional(),
  contactAddress: z.string().min(1).max(400).optional(),
  supportLink: z.object({ label: z.string().min(1).max(80), url: z.string().min(1).max(1000) }).strict().optional(),
};

const zTicketEmailSettingsBody = z
  .object({
    ...zTicketEmailSettingsFields,
    /** The ops config's contactEmail — where a buyer's reply lands (B63 (e2)), surfaced here. */
    replyTo: z.string().min(1).optional(),
    /** The tenant's trading name the defaults speak ("<Tenant> via Transact"). */
    tenantName: z.string().min(1).optional(),
    updatedAt: zEpochMs.optional(),
  })
  .strict();

export const emailSettingsGet = readOp({
  name: "email.settings.get",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: zTicketEmailSettingsBody,
  exampleRequest: {},
  exampleResponse: { tenantName: "Harbor Ferry", replyTo: "hello@harborferry.example" },
});

export const emailSettingsSet = writeOp({
  name: "email.settings.set",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: zTicketEmailSettingsFields,
  response: zTicketEmailSettingsBody,
  exampleRequest: { goodToKnow: "Board at Dock 3. Arrive 15 minutes before departure." },
  exampleResponse: { goodToKnow: "Board at Dock 3. Arrive 15 minutes before departure.", tenantName: "Harbor Ferry", updatedAt: 1_790_000_000_000 },
});

/**
 * B606 (f) — SEND ME A TEST: the sample order drawn with the form's (possibly
 * unsaved) settings, mailed ONLY to the signed-in staff member's own address
 * — never a typed one — at most once a minute per staff member.
 */
/**
 * B625 — the tenant's own sample order for SEND ME A TEST, drawn by the
 * admin from its catalog. Bounded: it only ever reaches the caller's own
 * sign-in address, and it is words + whole minor units, nothing else.
 */
const zTicketEmailSample = z
  .object({
    productName: z.string().min(1).max(120),
    when: z.object({ day: z.string().min(1).max(40), time: z.string().min(1).max(40) }).strict(),
    tiers: z
      .array(z.object({ name: z.string().min(1).max(80), unitPrice: z.number().int().nonnegative().max(100_000_000) }).strict())
      .min(1)
      .max(3),
  })
  .strict();

export const emailTestSend = writeOp({
  name: "email.test.send",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { ...zTicketEmailSettingsFields, channelId: zId.optional(), sample: zTicketEmailSample.optional() },
  response: z.object({ sentTo: z.string().min(1) }).strict(),
  exampleRequest: { subject: "Your tickets for {product} — {code}" },
  exampleResponse: { sentTo: "pat@harborferry.example" },
});

/**
 * B98 — the per-staff dashboard-prefs pair: the server-side home for
 * nudge dismissals (per-browser localStorage until now). PER-STAFF BY
 * CONSTRUCTION: no staffId field exists — the handler keys the store on
 * the AUTHENTICATED principal's own id, so a cross-staff read or write
 * is structurally impossible (the B25c fence stance, no fence rule
 * needed). Audience = the nudge audience exactly (MANAGER/ADMIN). The
 * WRITE rides ledger:read — the audience's own scope, the B113
 * intent.interpret precedent (a dedicated prefs scope would be an
 * authorizer amendment, deliberately not taken; flagged on TRA-148).
 * Full-replacement write, replay-safe under Inv 4's ordinary clientKey.
 */
const zStaffPrefsBody = z
  .object({ dismissedNudges: z.array(z.string().min(1).max(120)).max(200) })
  .strict();

export const staffPrefsGet = readOp({
  name: "staff.prefs.get",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: zStaffPrefsBody,
  exampleRequest: {},
  exampleResponse: { dismissedNudges: ["slow-weekday:tue"] },
});

export const staffPrefsSet = writeOp({
  name: "staff.prefs.set",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { dismissedNudges: z.array(z.string().min(1).max(120)).max(200) },
  response: zStaffPrefsBody,
  exampleRequest: { dismissedNudges: ["slow-weekday:tue", "unscanned:slot-9"] },
  exampleResponse: { dismissedNudges: ["slow-weekday:tue", "unscanned:slot-9"] },
});

/**
 * B104 (PII-1) — the erasure path for the one PII class the platform holds.
 * A write op is auth surface (§4.1, B20a discipline): ADMIN-only, on the
 * tenant-configuration scope (the B80 precedent — a dedicated scope is an
 * authorizer amendment, deliberately not taken). Tombstones the customer
 * row (email leaves the store; frozen facts never carried it, so reads go
 * honestly empty) and redacts delivery-log destinations beyond the resend
 * window; rows still inside it are counted in deliveriesRetained — re-run
 * with a fresh clientKey once the window ages out. The ledger is untouched
 * (Inv 1).
 */
export const customerErase = writeOp({
  name: "customer.erase",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { email: z.string().min(3) },
  response: z
    .object({
      erased: z.boolean(),
      customerId: zId.optional(),
      deliveriesRedacted: z.number().int().nonnegative(),
      deliveriesRetained: z.number().int().nonnegative(),
      /** B616 — the address's Insider row went too; absent where no list is wired. */
      subscriberErased: z.boolean().optional(),
    })
    .strict(),
  exampleRequest: { email: "pat@example.com" },
  exampleResponse: { erased: true, customerId: "cus-1", deliveriesRedacted: 2, deliveriesRetained: 0 },
});

/**
 * B500 leg (i) — the tenant's own flagged checkouts, for Live Operations'
 * Alerts card (B379). Ruled OBSERVATIONAL (Pathik 2026-09-23): these orders
 * went through; the flag is a fact for staff eyes, never a verdict. Same
 * seat as order.get's velocity field (MANAGER/ADMIN, the books scope) —
 * no fence moves. `watching: false` = the stage runs no counters, so an
 * empty list there is NOT an all-clear and the face must say so.
 */
export const orderVelocityFlags = readOp({
  name: "order.velocity_flags",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      watching: z.boolean(),
      /** The window read back — the last 24 hours. */
      lookbackMs: z.number().int().positive(),
      /** Newest first, bounded. */
      flagged: z.array(
        z
          .object({
            orderId: zId,
            at: zEpochMs,
            flags: z.array(zVelocityFlag).min(1),
            confirmationCode: z.string().min(1).optional(),
            total: zMoney.optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    watching: true,
    lookbackMs: 86_400_000,
    flagged: [
      {
        orderId: "order-c-41",
        at: 1_790_000_000_000,
        flags: [{ dimension: "buyer" as const, count: 4, windowMs: 600_000 }],
        confirmationCode: "HF7K-2Q9M",
        total: { amount: 48_00, currency: "USD" },
      },
    ],
  },
});

export const opsAlerts = readOp({
  name: "ops.alerts",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    day: zAlertDay,
    lookaheadDays: z.number().int().min(0).max(31).optional(),
    preset: z.enum(["ferry", "zoo", "attraction"]).optional(),
    thresholds: z
      .object({
        nearSelloutLeft: z.number().int().min(1).optional(),
        sellingBadlyLoadFactorBps: zBps.optional(),
        unscannedScanRateBps: zBps.optional(),
      })
      .strict()
      .optional(),
  },
  response: z
    .object({
      day: zAlertDay,
      alerts: z.array(
        z.discriminatedUnion("type", [
          z
            .object({
              type: z.literal("NEAR_SELLOUT"),
              ...zAlertBase,
              poolId: zId,
              left: z.number().int().nonnegative(),
              capacity: z.number().int().positive(),
            })
            .strict(),
          z
            .object({
              type: z.literal("SELLING_BADLY"),
              ...zAlertBase,
              sold: z.number().int().nonnegative(),
              capacity: z.number().int().positive(),
              loadFactorBps: zBps,
            })
            .strict(),
          z
            .object({
              type: z.literal("UNSCANNED_DEPARTURE"),
              ...zAlertBase,
              end: zEpochMs,
              sold: z.number().int().positive(),
              scanned: z.number().int().nonnegative(),
              scanRateBps: zBps,
            })
            .strict(),
          /* B648 (c) — yesterday's Stripe payouts and the ledger disagree: the day, how many findings, the first in words. */
          z
            .object({
              type: z.literal("RECONCILIATION_DISCREPANCY"),
              localDate: zAlertDay,
              count: z.number().int().positive(),
              first: z.string().min(1),
            })
            .strict(),
        ]),
      ),
      skipped: z.array(z.object({ type: z.literal("RECONCILIATION_DISCREPANCY"), why: z.string().min(1) }).strict()),
    })
    .strict(),
  exampleRequest: { day: "2026-07-09", preset: "ferry" },
  exampleResponse: {
    day: "2026-07-09",
    alerts: [
      {
        type: "NEAR_SELLOUT",
        slotId: "s-1",
        scheduleId: "sch-1",
        serviceId: "svc-1",
        localDate: "2026-07-09",
        start: 1_780_000_000_000,
        poolId: "s-1#seat",
        left: 3,
        capacity: 50,
      },
    ],
    skipped: [
      {
        type: "RECONCILIATION_DISCREPANCY",
        why: "provider payout ingest has no \u00a711 operation yet (rides with B30 wiring) \u2014 reconciliation was not consulted, not found clean",
      },
    ],
  },
});

/* ---------------------------------------------------- B462 drawer sessions */

/**
 * B462 — cash drawers get REGISTER IDENTITY and live on the wire.
 * Measured 2026-09-14: the drawer session was browser-local (no
 * register, no persistence — close reports died with the tab) and
 * RECONCILE AGAINST LEDGER compared one till against the TENANT-WIDE
 * sale journals. These ops are the cure's rails: a session binds to an
 * enrolled device row (the register — identity + location), every till
 * movement is appended per tenant, and the settlement is computed and
 * frozen at close so it survives the tab and feeds the money section's
 * per-register history. The LEDGER IS NOT TOUCHED (spec §16.2): the
 * session's own CASH_IN references name its orders, which is what
 * scopes reconciliation to this register without the book changing
 * shape. Audience: the till's own — SELLER runs the drawer (the same
 * seat that records tenders, payment:write), MANAGER/ADMIN beside;
 * reads ride order:read so the till can resume its register's open
 * session after a reload and the money section can read history.
 * sellerId never travels — the open handler keys it from the CALLING
 * principal (the B98 stance).
 */
const zDrawerMovementKind = z.enum(["CASH_IN", "CHANGE_OUT", "REFUND_OUT"]);
const zDrawerMovement = z
  .object({ kind: zDrawerMovementKind, amountMinor: z.number().int().positive(), at: zEpochMs, reference: zId })
  .strict();
const zDrawerEvent = z
  .object({ kind: z.enum(["OPEN", "CASH_IN", "CHANGE_OUT", "REFUND_OUT"]), amountMinor: z.number().int().nonnegative(), at: zEpochMs, reference: zId })
  .strict();
const zDrawerSettlement = z
  .object({
    expectedMinor: z.number().int(),
    countedMinor: z.number().int().nonnegative(),
    varianceMinor: z.number().int(),
    verdict: z.enum(["BALANCED", "OVER", "SHORT"]),
    closedAt: zEpochMs,
    note: z.string().min(1).max(500).optional(),
  })
  .strict();
const zDrawerSessionBody = z
  .object({
    sessionId: zId,
    registerId: zId,
    locationId: zId,
    sellerId: zId,
    currency: z.string().regex(/^[A-Z]{3}$/),
    sellerEmail: z.string().email().max(320).optional(),
    sellerRole: z.string().min(1).max(40).optional(),
    openingFloatMinor: z.number().int().nonnegative(),
    openedAt: zEpochMs,
    events: z.array(zDrawerEvent).max(2000),
    terminalTotalMinor: z.number().int().nonnegative(),
    terminalRefundTotalMinor: z.number().int().nonnegative(),
    compsTotalMinor: z.number().int().nonnegative(),
    admissionsSoldCount: z.number().int().nonnegative(),
    settlement: zDrawerSettlement.optional(),
    updatedAt: zEpochMs,
  })
  .strict();
const zDrawerSessionSummary = z
  .object({
    sessionId: zId,
    registerId: zId,
    locationId: zId,
    sellerId: zId,
    sellerEmail: z.string().email().max(320).optional(),
    sellerRole: z.string().min(1).max(40).optional(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    openingFloatMinor: z.number().int().nonnegative(),
    openedAt: zEpochMs,
    settlement: zDrawerSettlement.optional(),
  })
  .strict();

const DRAWER_AUDIENCE = ["STAFF:SELLER", "STAFF:MANAGER", "STAFF:ADMIN"] as const;

export const drawerOpen = writeOp({
  name: "drawer.open",
  permission: "payment:write",
  principals: [...DRAWER_AUDIENCE],
  fields: {
    sessionId: zId,
    registerId: zId,
    currency: z.string().regex(/^[A-Z]{3}$/),
    openingFloatMinor: z.number().int().nonnegative(),
    // B462 (g) — display-only words for the settlements face, the B463
    // operatorEmail precedent: the till sends its OWN token's email claim;
    // the kernel-verified principal stays the structural sellerId.
    sellerEmail: z.string().email().max(320).optional(),
  },
  // locationId deliberately does NOT travel: the register row carries it,
  // and a till that could claim a location would be a fence with a gap.
  response: zDrawerSessionBody,
  exampleRequest: { sessionId: "ds-1", registerId: "dev-1", currency: "USD", openingFloatMinor: 14_000 },
  exampleResponse: {
    sessionId: "ds-1",
    registerId: "dev-1",
    locationId: "loc-1",
    sellerId: "staff-7",
    currency: "USD",
    openingFloatMinor: 14_000,
    openedAt: 1_780_000_000_000,
    events: [{ kind: "OPEN", amountMinor: 14_000, at: 1_780_000_000_000, reference: "ds-1" }],
    terminalTotalMinor: 0,
    terminalRefundTotalMinor: 0,
    compsTotalMinor: 0,
    admissionsSoldCount: 0,
    updatedAt: 1_780_000_000_000,
  },
});

export const drawerRecord = writeOp({
  name: "drawer.record",
  permission: "payment:write",
  principals: [...DRAWER_AUDIENCE],
  fields: {
    sessionId: zId,
    events: z.array(zDrawerMovement).max(20),
    terminalDeltaMinor: z.number().int().nonnegative().optional(),
    terminalRefundDeltaMinor: z.number().int().nonnegative().optional(),
    compsDeltaMinor: z.number().int().nonnegative().optional(),
    admissionsDelta: z.number().int().nonnegative().optional(),
  },
  response: z.object({ sessionId: zId, events: z.number().int().positive(), expectedMinor: z.number().int() }).strict(),
  exampleRequest: {
    sessionId: "ds-1",
    events: [{ kind: "CASH_IN", amountMinor: 5_000, at: 1_780_000_060_000, reference: "o-1" }],
    admissionsDelta: 2,
  },
  exampleResponse: { sessionId: "ds-1", events: 2, expectedMinor: 19_000 },
});

export const drawerClose = writeOp({
  name: "drawer.close",
  permission: "payment:write",
  principals: [...DRAWER_AUDIENCE],
  fields: { sessionId: zId, countedMinor: z.number().int().nonnegative(), note: z.string().min(1).max(500).optional() },
  response: zDrawerSessionBody,
  exampleRequest: { sessionId: "ds-1", countedMinor: 18_500, note: "till roll jam" },
  exampleResponse: {
    sessionId: "ds-1",
    registerId: "dev-1",
    locationId: "loc-1",
    sellerId: "staff-7",
    currency: "USD",
    openingFloatMinor: 14_000,
    openedAt: 1_780_000_000_000,
    events: [{ kind: "OPEN", amountMinor: 14_000, at: 1_780_000_000_000, reference: "ds-1" }],
    terminalTotalMinor: 0,
    terminalRefundTotalMinor: 0,
    compsTotalMinor: 0,
    admissionsSoldCount: 0,
    settlement: { expectedMinor: 14_000, countedMinor: 18_500, varianceMinor: 4_500, verdict: "OVER", closedAt: 1_780_000_090_000 },
    updatedAt: 1_780_000_090_000,
  },
});

export const drawerGet = readOp({
  name: "drawer.get",
  permission: "order:read",
  principals: [...DRAWER_AUDIENCE],
  fields: { sessionId: zId },
  response: z.object({ session: zDrawerSessionBody.optional() }).strict(),
  exampleRequest: { sessionId: "ds-1" },
  exampleResponse: { session: undefined },
});

export const drawerList = readOp({
  name: "drawer.list",
  permission: "order:read",
  principals: [...DRAWER_AUDIENCE],
  fields: { registerId: zId.optional(), state: z.enum(["OPEN", "CLOSED"]).optional() },
  response: z.object({ sessions: z.array(zDrawerSessionSummary).max(200) }).strict(),
  exampleRequest: { registerId: "dev-1", state: "OPEN" },
  exampleResponse: { sessions: [] },
});

/**
 * B462 (a) — the till's register pick: the enrolled device rows
 * (identity + location) a counter can BE. Deliberately its own door
 * rather than widening device.list (that read stays ADMIN, its rows
 * carry fleet administration); this one answers only what a drawer
 * binding needs and rides the till's own order:read.
 */
export const drawerRegisters = readOp({
  name: "drawer.registers",
  permission: "order:read",
  principals: [...DRAWER_AUDIENCE],
  fields: {},
  response: z
    .object({
      registers: z.array(
        z.object({ registerId: zId, role: z.string().min(1), locationId: zId, lastSeenAt: zEpochMs.optional() }).strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: { registers: [{ registerId: "dev-1", role: "TERMINAL", locationId: "loc-1" }] },
});
